import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { createNewChat, setUnreadCount } from 'actions/supportAssistant/supportAssistantActions'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import TextButton from 'LuxKit/Button/TextButton'
import LinePlusIcon from 'LuxKit/Icons/line/LinePlusIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import Group from 'components/utils/Group'
import { rem } from 'polished'
import styled from 'styled-components'
import HeadData from 'components/Common/HeadData'
import MessagesListContainer from 'supportAssistant/components/Chat/MessagesListContainer'
import useChatState from 'supportAssistant/hooks/useChatState'
import useTabVisibility from 'hooks/useTabVisibility'
import { selectCurrentChat, selectOutageMessage } from 'supportAssistant/selectors/selectCurrentChat'
import { getUnreadCount } from 'selectors/supportAssistantSelectors'
import Agent from '../Support/Agent'
import MetaComponents from './MessageMeta/MetaComponents'
import ChatEmptyState from 'supportAssistant/components/Chat/ChatEmptyState'
import { set } from 'lib/storage/isomorphicLocalStorage'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyFeatureFlags } from 'constants/optimizely'

const StartNewChatContainer = styled(Group)`
  padding-left: ${rem(32)};
`

const ActionsSkeleton = styled(LoadingBox)`
  align-self: start;
  height: ${rem(40)};
  margin-top: ${rem(12)};
  margin-left: ${rem(32)};
`

const ScrollTo = styled.div`
  padding-bottom: ${rem(24)};
`

export interface ChatContentProps {
  compact?: boolean
}

function ChatContent(props: ChatContentProps) {
  const { compact = false } = props

  const [messages, typing, agentTyping, fetchingChatMessages] = useChatState()
  const hasUserMessage = messages.some(m => m.sender === 'user')
  const isEmptyState = !fetchingChatMessages && !hasUserMessage
  const dispatch = useAppDispatch()
  const isTabVisible = useTabVisibility()

  const currentChat = useAppSelector(state => selectCurrentChat(state))
  const outageMessage = useAppSelector(state => selectOutageMessage(state))
  const unreadMessageCount = useAppSelector(getUnreadCount)

  const lastMessage = messages[messages.length - 1]
  const isLastMessageByUser = lastMessage?.sender === 'user'

  const showChatActions = !isEmptyState && !fetchingChatMessages && lastMessage && currentChat?.status !== 'CLOSED' && lastMessage.sender === 'chatbot'

  const isLESAConditionalBubbleEnabled = !!useOptimizelyExperiment(OptimizelyFeatureFlags.opexLESAConditionalBubble)

  // Document title management for page layout
  const [showDocumentTitleMessage, setShowDocumentTitleMessage] = useState(false)
  const lastSeenMessageForTitle = useRef(lastMessage)

  useEffect(() => {
    if (!compact && !isTabVisible && lastSeenMessageForTitle?.current?.id !== lastMessage?.id) {
      setShowDocumentTitleMessage(true)
      lastSeenMessageForTitle.current = lastMessage
    }
  }, [isTabVisible, lastMessage, messages.length, compact])

  useEffect(() => {
    if (!compact && (isLastMessageByUser || isTabVisible)) {
      setShowDocumentTitleMessage(false)
    }
  }, [isLastMessageByUser, isTabVisible, messages, compact])

  const handleNewChatClick = useCallback(() => {
    dispatch(createNewChat())
  }, [dispatch])

  const scrollRef = useRef<HTMLInputElement>(null)

  const scrollToBottom = () => {
    scrollRef.current?.scrollIntoView({ behavior: 'instant', block: 'nearest', inline: 'end' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages.length])

  const lastSeenMessage = useRef(lastMessage)
  const hasRenderedMessages = useRef(false)
  const LAST_SEEN_PREFIX = 'lastSeenAt-'

  // Reset the rendered flag when chat changes
  useEffect(() => {
    hasRenderedMessages.current = false
  }, [currentChat?.id])

  useEffect(() => {
    if (!isTabVisible && lastSeenMessage?.current?.id !== lastMessage?.id) {
      lastSeenMessage.current = lastMessage
    }
  }, [isTabVisible, lastMessage, messages.length])

  // Mark messages as read when messages are rendered and the tab is visible
  const handleMessagesRendered = useCallback(() => {
    hasRenderedMessages.current = true
    if (currentChat?.id && isTabVisible && isLESAConditionalBubbleEnabled) {
      set(`${LAST_SEEN_PREFIX}${currentChat.id}`, new Date().toISOString())
      dispatch(setUnreadCount(0))
    }
  }, [currentChat?.id, isTabVisible, isLESAConditionalBubbleEnabled, dispatch])

  // Mark messages as read when messages are already rendered and the tab becomes visible
  useEffect(() => {
    if (isTabVisible && hasRenderedMessages.current && currentChat?.id && !isEmptyState && isLESAConditionalBubbleEnabled) {
      set(`${LAST_SEEN_PREFIX}${currentChat.id}`, new Date().toISOString())
      dispatch(setUnreadCount(0))
    }
  }, [isTabVisible, currentChat?.id, isEmptyState, isLESAConditionalBubbleEnabled, dispatch])

  const emptyStateClassName = compact ? 'chat-empty-state-container' : undefined

  return (
    <>
      {!compact && isLESAConditionalBubbleEnabled && showDocumentTitleMessage && unreadMessageCount > 0 && (
        <HeadData defer={false} title={`(${unreadMessageCount}) Luxury Escapes`} />
      )}

      <Group
        direction="vertical"
        verticalAlign={isEmptyState && compact ? 'center' : 'start'}
        gap={16}
        fullHeight={isEmptyState && compact}
      >
        {fetchingChatMessages && <ActionsSkeleton />}

        {!fetchingChatMessages && isEmptyState && (
          <ChatEmptyState
            isTyping={typing}
            actions={messages[0]?.meta?.content}
            payload={messages[0]?.meta?.payload}
            className={emptyStateClassName}
          />
        )}

        {!fetchingChatMessages && !isEmptyState && (
          <>
            <MessagesListContainer
              loadingMessages={fetchingChatMessages}
              messages={messages}
              isTyping={typing}
              isAgentTyping={agentTyping}
              onMessagesRendered={handleMessagesRendered}
              compact={compact}
            />
            {showChatActions && lastMessage?.meta?.content && (
              <MetaComponents
                metaContent={lastMessage.meta.content}
                payload={lastMessage?.meta?.payload}
              />
            )}
          </>
        )}

        {!fetchingChatMessages && currentChat?.status === 'CLOSED' && (
          <StartNewChatContainer fullWidth horizontalAlign="start" direction="vertical" gap={8}>
            <BodyTextBlock variant="small">
              This chat is timed out. Please start a new chat.
            </BodyTextBlock>

            <TextButton
              kind="secondary"
              variant="dark"
              onClick={handleNewChatClick}
              startIcon={<LinePlusIcon />}
            >
              Start new chat
            </TextButton>
          </StartNewChatContainer>
        )}

        {!isEmptyState && <Agent loadingMessages={fetchingChatMessages} />}
        {!isEmptyState && <ScrollTo ref={scrollRef} />}

        {!fetchingChatMessages && outageMessage && (
          <MessageBanner kind="critical">{outageMessage}</MessageBanner>
        )}
      </Group>
    </>
  )
}

export default ChatContent
